import {Component, OnDestroy, OnInit} from '@angular/core';
import {Invoice} from '../../model/invoice';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {InvoicePeerService} from '../../service/invoice-peer.service';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {map, takeUntil, tap} from 'rxjs/operators';
import { MatSelectChange } from '@angular/material/select';
import {UsersMap, UsersMapCache} from '../../model/model-map-cache';
import {InvoicesService} from '../../api/services/invoices.service';
import {TextMessage} from '../../api/models/text-message';
import {CreateTextMessageRequest} from '../../api/models/create-text-message-request';
import {TextMessageEvent} from '../../api/models/text-message-event';
import {phone} from 'phone';
import { RealtimeNotificationsService } from 'src/app/service/realtime-notifications.service';
import {TextMessageTemplate} from '../../api/models/text-message-template';
import {TextMessageTemplatesService} from '../../api/services/text-message-templates.service';

// AI: Custom validator to check for a valid phone number.
export function phoneNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) {
      return null;
    }
    // AI: If the number starts with a +, let the phone library detect the country. Otherwise, assume it's a Finnish number.
    const country = typeof value === 'string' && value.startsWith('+') ? null : 'FIN';
    const {isValid} = phone(value, {country});
    return isValid ? null : {invalidPhoneNumber: true};
  };
}

@Component({
  selector: 'app-invoice-sms-widget',
  templateUrl: './invoice-sms-widget.component.html',
  styleUrls: ['./invoice-sms-widget.component.scss']
})
export class InvoiceSmsWidgetComponent implements OnInit, OnDestroy {

  invoice$: Observable<Invoice>;
  private readonly textMessagesSubject = new BehaviorSubject<TextMessage[]>([]);
  textMessages$ = this.textMessagesSubject.asObservable();
  smsTemplates$: Observable<TextMessageTemplate[]>;
  usersMap$: Observable<UsersMap>;

  invoice: Invoice;
  selectedItem: TextMessage;

  form: UntypedFormGroup;
  locked = false;
  added = false;
  saving = false;

  private readonly ngDestroy$ = new Subject<void>();

  constructor(
    private invoicePeerService: InvoicePeerService,
    private invoicesService: InvoicesService,
    private textMessageTemplatesService: TextMessageTemplatesService,
    private usersMapCache: UsersMapCache,
    private builder: UntypedFormBuilder,
    private realtime: RealtimeNotificationsService) {
    this.form = this.builder.group({
      smsTemplateId: [null],
      // AI: Added the custom phone number validator.
      recipient: [null, [Validators.required, phoneNumberValidator()]],
      message: [null, Validators.required],
    });
  }

  add(): void {
    this.invoicePeerService.lock();
    this.added = true;
    this.form.reset();
  }

  revert(): void {
    this.invoicePeerService.unlock();
    this.added = false;
  }

  save(): void {
    this.saving = true;
    const request: CreateTextMessageRequest = {
      recipient: this.form.value.recipient,
      content: this.form.value.message
    };
    this.invoicesService.createTextMessage({id: this.invoice.invoiceId, body: request}).subscribe(() => {
      this.invoicePeerService.unlock();
      this.selectedItem = null;
      this.saving = false;
      this.added = false;
      this.invoicesService.listTextMessages({id: this.invoice.invoiceId})
        .subscribe(messages => this.textMessagesSubject.next(messages));
    });
  }

  itemClicked(item: TextMessage) {
    this.selectedItem = (item !== this.selectedItem ? item : null);
  }

  templateChange(event: MatSelectChange) {
    const template: TextMessageTemplate = event.value;

    if (!template) {
      this.form.patchValue({message: null});
      return;
    }

    this.textMessageTemplatesService.renderTextMessageTemplate({
      body: {
        invoice_id: this.invoice.invoiceId,
        template_id: template.id
      }
    }).subscribe(rendered => {
      this.form.patchValue({message: rendered.content});
    });
  }

  getSortedEvents(events: TextMessageEvent[]): TextMessageEvent[] {
    if (!events) {
      return [];
    }
    return [...events].sort((a, b) => new Date(a.event_at).getTime() - new Date(b.event_at).getTime());
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'delivered':
      case 'legacy_success':
        return 'status-success';
      case 'legacy_failure':
      case 'rejected':
      case 'failed':
        return 'status-failure';
      default:
        return '';
    }
  }

  ngOnInit() {

    this.usersMap$ = this.usersMapCache.map$;

    this.smsTemplates$ = this.getSmsTemplates();

    this.invoice$ = this.invoicePeerService
      .get()
      .pipe(
        tap(invoice => {
            this.invoice = invoice;
            if (invoice) {
              this.invoicesService.listTextMessages({id: invoice.invoiceId})
                .subscribe(messages => this.textMessagesSubject.next(messages));
            } else {
              this.textMessagesSubject.next([]);
            }
          }
        ));

    this.invoicePeerService.getState().pipe(takeUntil(this.ngDestroy$)).subscribe((state) => {
      if (state === 'locked') {
        this.locked = true;
      } else if (state === 'unlocked') {
        this.locked = false;
      }
    });

    this.realtime.events$
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe(event => {
        if (
          event.event_type === 'text_message_updated' &&
          this.invoice &&
          event.invoice_id === this.invoice.invoiceId
        ) {
          this.invoicesService.listTextMessages({ id: this.invoice.invoiceId })
            .subscribe(messages => this.textMessagesSubject.next(messages));
        }
      });
  }

  formatPhoneNumberOnBlur(): void {
    const recipientControl = this.form.get('recipient');
    const value = recipientControl.value;
    if (typeof value === 'string') {
      // AI: If the number starts with a +, let the phone library detect the country. Otherwise, assume it's a Finnish number.
      const country = value.startsWith('+') ? null : 'FIN';
      const {isValid, phoneNumber} = phone(value, {country});
      if (isValid) {
        recipientControl.setValue(phoneNumber);
      }
    }
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  private getSmsTemplates(): Observable<TextMessageTemplate[]> {
    return this.textMessageTemplatesService.listTextMessageTemplates().pipe(
      // AI: Sort templates by name client-side as the API doesn't support it.
      map(templates => templates.sort((a, b) => a.name.localeCompare(b.name)))
    );
  }
}
